/**
 * useTenantWorkspacesPipelines - Lista TODAS as workspaces do tenant
 * com suas pipelines e fases. Usado para permitir mover um negócio
 * de uma workspace para outra na edição do deal.
 *
 * Versão: 1.0.0
 * RELEASE: 2026-07-11
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('TenantWorkspacesPipelines');

// Fase mínima usada no seletor
export interface WsPipelinePhase {
  id: string;
  name: string;
  color: string | null;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
}

// Pipeline aninhado dentro do workspace
export interface WsPipeline {
  id: string;
  name: string;
  sort_order: number;
  workspace_id: string;
  phases: WsPipelinePhase[];
}

// Workspace com suas pipelines
export interface WsWithPipelines {
  id: string;
  name: string;
  pipelines: WsPipeline[];
}

/**
 * Busca todas as workspaces do tenant atual junto com suas pipelines ativas
 * e fases ordenadas. Habilitado apenas quando `enabled=true` para lazy loading.
 */
export function useTenantWorkspacesPipelines(options?: { enabled?: boolean }) {
  const { currentTenant } = useAuth();
  const isEnabled = (options?.enabled ?? true) && !!currentTenant?.id;

  return useQuery({
    queryKey: ['tenant-workspaces-pipelines', currentTenant?.id],
    queryFn: async (): Promise<WsWithPipelines[]> => {
      if (!currentTenant?.id) return [];

      logger.debug('Buscando workspaces + pipelines do tenant');

      // Busca workspaces do tenant
      const { data: workspaces, error: wsErr } = await supabase
        .from('crm_workspaces')
        .select('id, name')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');

      if (wsErr) {
        logger.error('Erro ao buscar workspaces:', wsErr.message);
        throw wsErr;
      }

      const { data: pipelines, error: pErr } = await supabase
        .from('crm_pipelines')
        .select(`
          id,
          name,
          sort_order,
          workspace_id,
          crm_pipeline_phases(
            id,
            name,
            color,
            sort_order,
            is_won,
            is_lost
          )
        `)
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('sort_order');

      if (pErr) {
        logger.error('Erro ao buscar pipelines:', pErr.message);
        throw pErr;
      }

      // Agrupa pipelines por workspace
      return (workspaces || []).map((ws) => ({
        id: ws.id,
        name: ws.name,
        pipelines: (pipelines || [])
          .filter((p: any) => p.workspace_id === ws.id)
          .map((p: any) => ({
            id: p.id,
            name: p.name,
            sort_order: p.sort_order,
            workspace_id: p.workspace_id,
            phases: (p.crm_pipeline_phases || [])
              .sort((a: any, b: any) => a.sort_order - b.sort_order),
          })),
      }));
    },
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });
}
